import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "store/store";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  labelDi,
  labelElement,
  labelElementsViewed,
  labelUtente,
  labelId,
  labelDate,
  labelState,
  labelReportsEmpty,
  labelNoResults,
} from "_constants";
import {
  EmptyMessageTable,
  IconSortTable,
  ReportStateChip,
} from "components/shared";
import { Dropdown } from "primereact/dropdown";
import { formatDataSource } from "utils/utils";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { handleSort } from "utils";
import {
  resetFiltersReports,
  setIsLoadingReports,
  setReports,
  setPageReportsAndSize,
  setSortingReports,
} from "store/slicer/GestioneReportSlice";

import { getAllReports, getDetailReport } from "services";
import { PrimeIcons } from "primereact/api";
import { useDetailPanelProvider } from "providers/DetailPanelProvider";
import { ReportDetailPanelHeader } from "./ReportDetailPanelHeader";
import { ReportDetailPanelBody } from "./ReportDetailPanelBody";
import { ReportDetailPanelFooter } from "./ReportDetailPanelFooter";
import { Tooltip } from "primereact/tooltip";

export const ReportsTable = () => {
  const dispatch = useAppDispatch();
  const [totalCount, setTotalCount] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const { showPanel } = useDetailPanelProvider();
  const filters = useAppSelector((state) => state.gestioneReportSlice.filters);
  const isLoadingReports = useAppSelector(
    (state) => state.gestioneReportSlice.isLoadingReports
  );
  const reportsList = useAppSelector(
    (state) => state.gestioneReportSlice.reportList
  );
  const sortedDirection = useAppSelector(
    (state) => state.gestioneReportSlice.sortedDirection
  );
  const sortedColumn = useAppSelector(
    (state) => state.gestioneReportSlice.sortedColumn
  );

  useEffect(() => {
    return () => {
      dispatch(resetFiltersReports());
    };
  }, [dispatch]);

  useEffect(() => {
    if (filters.pageIndex > -1) {
      dispatch(setIsLoadingReports(true));
      getAllReports(filters)
        .then((response) => {
          setTotalCount(response.data.totalCount);
          dispatch(setReports(response.data.items as any));
        })
        .catch((_) => {})
        .finally(() =>
          setTimeout(() => {
            setFirstLoad(false);
            dispatch(setIsLoadingReports(false));
          }, 500)
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const onPage = (event: DataTableStateEvent) => {
    if (event.page || event.rows) {
      dispatch(
        setPageReportsAndSize({
          page: event.page ? event.page + 1 : 1,
          size: event.rows,
        })
      );
    }
  };

  const callHandleSort = (field: String) => {
    handleSort(
      sortedColumn,
      sortedDirection,
      dispatch,
      setSortingReports,
      field
    );
  };

  const template2 = {
    layout:
      "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
    RowsPerPageDropdown: (options: any) => {
      const dropdownOptions = [
        { label: 5, value: 5 },
        { label: 10, value: 10 },
        { label: 25, value: 25 },
      ];

      return (
        <Dropdown
          value={options.value}
          options={dropdownOptions}
          onChange={options.onChange}
        />
      );
    },
    CurrentPageReport: (options: any) => {
      const first = (filters.pageIndex - 1) * filters.pageSize + 1;
      const last =
        (filters.pageIndex - 1) * filters.pageSize + filters.pageSize;

      return (
        <>
          <span className="xs-none">
            {labelElementsViewed}
            {first} - {last} {labelDi}
            {totalCount} {labelElement}
            {options.totalRecords > 1 ? "i" : "o"}
          </span>
        </>
      );
    },
  };

  const openDetailPanel = (id: string) => {
    getDetailReport(id)
      .then((response) => {
        showPanel({
          header: <ReportDetailPanelHeader report={response.data} />,
          body: <ReportDetailPanelBody report={response.data} />,
          footer: <ReportDetailPanelFooter report={response.data} />,
        });
      })
      .catch((_) => {});
  };

  const getEmptyMessageTableLabel = () => {
    return isLoadingReports
      ? ""
      : filters.pageIndex === 1 &&
        !filters.globalSearch?.length &&
        !filters.state &&
        !filters.sorting
      ? labelReportsEmpty
      : labelNoResults;
  };

  return (
    <>
      <DataTable
        value={reportsList}
        size="small"
        lazy
        dataKey="id"
        paginator={reportsList?.length ? true : false}
        rows={filters.pageSize}
        totalRecords={totalCount}
        scrollable
        scrollHeight={`calc(100vh - ${reportsList?.length ? "240" : "180"}px)`}
        onPage={onPage}
        first={filters.pageIndex * filters.pageSize - 1}
        paginatorClassName="flex-container justify-end"
        className={`margin-18 custom-table-reports ${
          reportsList.length ? "has-element" : ""
        }`}
        tableStyle={{ minWidth: reportsList?.length ? "1332px" : "50vw" }}
        emptyMessage={
          !firstLoad ? (
            <EmptyMessageTable label={getEmptyMessageTableLabel()} />
          ) : (
            <div></div>
          )
        }
        paginatorTemplate={template2}
      >
        <Column
          field="code"
          bodyClassName={(rowData) => {
            return rowData.state === "ARCHIVIATA"
              ? "column-report-archived"
              : "";
          }}
          header={
            <IconSortTable
              nomeCampo={labelId}
              onSortCallback={() =>
                isLoadingReports ? null : callHandleSort("code")
              }
              iconStatus={
                sortedColumn !== "code"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingReports ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : null
          }
        />
        <Column
          field="createdBy"
          className="column-description"
          bodyClassName={(rowData) => {
            return rowData.state === "ARCHIVIATA"
              ? "column-report-archived"
              : "";
          }}
          header={
            <IconSortTable
              nomeCampo={labelUtente}
              onSortCallback={() =>
                isLoadingReports ? null : callHandleSort("createdBy")
              }
              iconStatus={
                sortedColumn !== "createdBy"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingReports ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <>
                  <Tooltip
                    target={`.created-by-report`}
                    mouseTrack
                    mouseTrackLeft={10}
                  />

                  <div
                    className={`created-by-report ellipsis fit-content`}
                    data-pr-tooltip={rowData.createdBy}
                  >
                    <span className="capitalize">{rowData.createdBy}</span>
                  </div>
                </>
              )
            )
          }
        />
        <Column
          field="creationDate"
          bodyClassName={(rowData) => {
            return rowData.state === "ARCHIVIATA"
              ? "column-report-archived"
              : "";
          }}
          header={
            <IconSortTable
              nomeCampo={labelDate}
              onSortCallback={() =>
                isLoadingReports ? null : callHandleSort("creationDate")
              }
              iconStatus={
                sortedColumn !== "creationDate"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingReports ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => <>{formatDataSource(rowData.creationDate)}</>
            )
          }
        />
        <Column
          field="state"
          bodyClassName={(rowData) => {
            return rowData.state === "ARCHIVIATA"
              ? "column-report-archived"
              : "";
          }}
          header={
            <IconSortTable
              nomeCampo={labelState}
              onSortCallback={() =>
                isLoadingReports ? null : callHandleSort("state")
              }
              iconStatus={
                sortedColumn !== "state"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingReports ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <div className="flex-container justify-between align-center custom-body-table">
                  <ReportStateChip type={rowData.state} />
                  <Button
                    text
                    onClick={() => openDetailPanel(rowData.id)}
                    type="button"
                    severity="secondary"
                    className="width-px-28 height-px-28"
                    icon={PrimeIcons.EYE}
                  />
                </div>
              )
            )
          }
        />
      </DataTable>
    </>
  );
};
